import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    UserX,
    FileText,
    Award,
    ClipboardCheck,
    LayoutDashboard,
    CheckSquare
} from 'lucide-react';
import ModuleLayout from '../../layouts/ModuleLayout';
import Drawer from '../../components/common/Drawer';
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';

// Importing existing production components
import {
    ExitRequests,
    NoDuesClearance,
    RelievingLetter,
    ExperienceCertificate
} from './exit-management';
import FullFinalSettlement from './payroll/FullFinalSettlement';

const ExitModule = ({ initialTab, initialAction, initialActionId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // STRICT TAB CONFIGURATION (Labels match production exactly)
    const EXIT_TABS = [
        { id: 'exitRequests', label: 'Exit Requests', icon: UserX, permission: 'exit_management' },
        { id: 'noDuesClearance', label: 'No Dues Clearance', icon: ClipboardCheck, permission: 'exit_management' },
        { id: 'relievingLetter', label: 'Relieving Letters', icon: FileText, permission: 'exit_management' },
        { id: 'experienceCertificate', label: 'Experience Letters', icon: Award, permission: 'exit_management' },
        { id: 'fullAndFinalSettlement', label: 'Full & Final Settlement', icon: CheckSquare, permission: 'exit_management' },
    ];

    // Canonical key derived from URL path or param
    const getTabFromPath = useCallback((path) => {
        if (path.includes('/exit_requests')) return 'exitRequests';
        if (path.includes('/no_dues')) return 'noDuesClearance';
        if (path.includes('/relieving_letter')) return 'relievingLetter';
        if (path.includes('/experience_certificate')) return 'experienceCertificate';
        if (path.includes('/full_final')) return 'fullAndFinalSettlement';
        return null;
    }, []);

    const getDrawerActionFromPath = useCallback((path) => {
        // Simple path check for details
        if (path.includes('/relieving_letter/')) return 'generateRelieving';
        if (path.includes('/experience_certificate/')) return 'generateExperience';
        return null;
    }, []);

    // Local state synchronized with URL
    const [activeTab, setActiveTab] = useState(() => {
        return searchParams.get('tab') || initialTab || getTabFromPath(location.pathname) || 'exitRequests';
    });
    const [action, setAction] = useState(() => {
        return searchParams.get('action') || initialAction || getDrawerActionFromPath(location.pathname) || null;
    });
    const [actionId, setActionId] = useState(() => {
        return searchParams.get('id') || initialActionId || (location.pathname.split('/').pop() !== 'exit' ? location.pathname.split('/').pop() : null);
    });

    // Synchronizers
    useEffect(() => {
        const tab = searchParams.get('tab') || getTabFromPath(location.pathname);
        if (tab && tab !== activeTab) setActiveTab(tab);

        const act = searchParams.get('action') || getDrawerActionFromPath(location.pathname);
        const aid = searchParams.get('id') || (location.pathname.split('/').pop() !== 'exit' ? location.pathname.split('/').pop() : null);

        setAction(act);
        setActionId(aid);
    }, [location.pathname, searchParams, getTabFromPath, getDrawerActionFromPath, activeTab]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSearchParams(prev => {
            const params = new URLSearchParams(prev);

            // Always set the active tab
            params.set('tab', tabId);

            // Clear any drawer-related params
            params.delete('action');
            params.delete('id');

            // Clear cross-tab filter/query params so each tab starts fresh
            const filterKeys = [
                'search',
                'status',
                'department',
                'resignationType',
                'startDate',
                'endDate',
                'page',
                'limit',
                'sortBy',
                'sortOrder',
            ];
            filterKeys.forEach((key) => params.delete(key));

            return params;
        });
    };

    const closeDrawer = () => {
        setSearchParams(prev => {
            prev.delete('action');
            prev.delete('id');
            return prev;
        });
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'exitRequests':
                return <ExitRequests />;
            case 'noDuesClearance':
                return <NoDuesClearance />;
            case 'relievingLetter':
                return <RelievingLetter />;
            case 'experienceCertificate':
                return <ExperienceCertificate />;
            case 'fullAndFinalSettlement':
                return <FullFinalSettlement />;
            default:
                return <ExitRequests />;
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'createExitRequest': return 'New Request';
            case 'viewExitCase': return 'Exit Case Details';
            case 'generateRelieving': return 'Relieving Letter Details';
            case 'generateExperience': return 'Experience Certificate Details';
            default: return 'Details';
        }
    };

    const renderDrawerContent = () => {
        if (!action) return null;

        switch (action) {
            case 'createExitRequest':
                // Check if ExitRequests supports initialMode
                return <ExitRequests initialMode="create" onClose={closeDrawer} isDrawer />;
            case 'viewExitCase':
                return <ExitRequests initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'generateRelieving':
                return <RelievingLetter initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            case 'generateExperience':
                return <ExperienceCertificate initialMode="view" id={actionId} onClose={closeDrawer} isDrawer />;
            default:
                return null;
        }
    };

    return (
        <PermissionGate permission="exit_management">
            <ModuleLayout
                title="Exit Management"
                tabs={EXIT_TABS}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            >
                {renderTabContent()}

                <Drawer
                    isOpen={!!action}
                    onClose={closeDrawer}
                    title={getDrawerTitle()}
                    size={(action === 'createExitRequest') ? 'full' : 'xl'}
                >
                    {renderDrawerContent()}
                </Drawer>
            </ModuleLayout>
        </PermissionGate>
    );
};

export default ExitModule;
